import { CalendarDays } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { initialsOf, type PeoplePickerProfile } from '@/components/projects/PeoplePicker';
import {
  DUE_TONE,
  dueState,
  formatDueShort,
  projectAccent,
  projectInitials,
} from '@/features/projects/display';
import { cn } from '@/lib/utils';

const MONOGRAM_SIZE = {
  sm: 'h-8 w-8 text-[11px]',
  md: 'h-10 w-10 text-xs',
  lg: 'h-12 w-12 text-sm',
} as const;

/**
 * Square brand-coloured tile with the project's initials. Gives each
 * project a stable visual anchor in grids and headers.
 */
export function ProjectMonogram({
  id,
  name,
  size = 'md',
  muted,
  className,
}: {
  id: string;
  name: string;
  size?: keyof typeof MONOGRAM_SIZE;
  muted?: boolean;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg font-medium tracking-wide',
        MONOGRAM_SIZE[size],
        muted ? 'bg-muted text-muted-foreground' : projectAccent(id),
        className,
      )}
    >
      {projectInitials(name)}
    </div>
  );
}

/**
 * Thin progress bar in Bright Moss. `warning` switches the fill to
 * Fresh Salmon for "past due with open work"; `onDark` is for Humus
 * hero surfaces.
 */
export function CompletionBar({
  value,
  tone = 'default',
  className,
  label = 'Completion',
}: {
  value: number;
  tone?: 'default' | 'warning' | 'onDark';
  className?: string;
  label?: string;
}) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      className={cn(
        'h-1.5 w-full overflow-hidden rounded-full',
        tone === 'onDark' ? 'bg-linen/15' : 'bg-bright-moss/30',
        className,
      )}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-500',
          tone === 'onDark'
            ? 'bg-bright-moss'
            : tone === 'warning'
              ? 'bg-fresh-salmon'
              : 'bg-bright-moss-dark',
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/**
 * Due-date pill: Fresh Salmon when overdue, Atmosphere when due today,
 * neutral otherwise. Completed tasks drop the alert tone.
 */
export function DueChip({
  due,
  completed,
  className,
}: {
  due: string | null | undefined;
  completed?: boolean;
  className?: string;
}) {
  if (!due) return null;
  const state = completed ? 'upcoming' : dueState(due);
  const label =
    state === 'today'
      ? 'Today'
      : state === 'overdue'
        ? `Overdue · ${formatDueShort(due)}`
        : formatDueShort(due);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs',
        DUE_TONE[state],
        state !== 'upcoming' && 'font-medium',
        className,
      )}
    >
      <CalendarDays className="h-3 w-3" strokeWidth={2} aria-hidden />
      {label}
    </span>
  );
}

const STACK_SIZE = {
  xs: { avatar: 'h-6 w-6', text: 'text-[9px]', overflow: 'text-[10px]' },
  sm: { avatar: 'h-7 w-7', text: 'text-[10px]', overflow: 'text-[11px]' },
} as const;

/** Overlapping avatar discs with a "+n" overflow chip. */
export function AvatarStack({
  people,
  max = 4,
  size = 'xs',
  className,
}: {
  people: PeoplePickerProfile[];
  max?: number;
  size?: keyof typeof STACK_SIZE;
  className?: string;
}) {
  if (people.length === 0) return null;
  const visible = people.slice(0, max);
  const overflow = people.length - visible.length;
  const s = STACK_SIZE[size];
  return (
    <div className={cn('flex -space-x-1.5', className)}>
      {visible.map((p) => (
        <Avatar
          key={p.id}
          className={cn(s.avatar, 'border-2 border-card')}
          title={p.full_name ?? p.email}
        >
          {p.avatar_url && <AvatarImage src={p.avatar_url} alt="" />}
          <AvatarFallback className={s.text}>{initialsOf(p)}</AvatarFallback>
        </Avatar>
      ))}
      {overflow > 0 && (
        <div
          className={cn(
            s.avatar,
            s.overflow,
            'z-10 flex items-center justify-center rounded-full border-2 border-card bg-secondary font-medium text-muted-foreground',
          )}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}
